import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Wallet,
  Plus,
  ArrowUpRight,
  ArrowDownLeft,
  TrendingUp,
  Calendar,
  ExternalLink,
  Loader2,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import {
  getBalance,
  getTransactions,
  WalletBalance,
  WalletTransaction,
} from "@/services/walletService";
import { formatDistanceToNow } from "date-fns";
import { formatCurrency } from "@/utils/currency";

interface WalletPopoverProps {
  children: React.ReactNode; // The button that triggers the popover
}

export const WalletPopover: React.FC<WalletPopoverProps> = ({ children }) => {
  const params = useParams();
  const navigate = useNavigate();
  const storeType = params.storeType as string | undefined;
  const storeId = params.storeId as string | undefined;

  const [isOpen, setIsOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(
    null,
  );
  const [recentTransactions, setRecentTransactions] = useState<
    WalletTransaction[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadWalletData = async (showRefreshLoader = false) => {
    try {
      setError(null);

      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const [balanceResponse, transactionsResponse] = await Promise.all([
        getBalance(),
        getTransactions(1, 5), // Get last 5 transactions
      ]);

      // Extract data from API responses
      setWalletBalance(balanceResponse.data as WalletBalance);
      setRecentTransactions(transactionsResponse.data as WalletTransaction[]);
    } catch (error: any) {
      console.error("Failed to load wallet data:", error);
      setError(error?.message || "Failed to load wallet data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load data when popover opens
  useEffect(() => {
    if (isOpen && !walletBalance) {
      loadWalletData();
    }
  }, [isOpen]);

  const handleAddMoney = () => {
    if (storeType && storeId) {
      setIsOpen(false);
      navigate(`/${storeType}/${storeId}/wallet?action=add`);
    }
  };

  const handleViewFullWallet = () => {
    if (storeType && storeId) {
      setIsOpen(false);
      navigate(`/${storeType}/${storeId}/wallet`);
    }
  };

  const getTransactionIcon = (type: string) => {
    return type === "credit" || type === "add" || type === "refund" ? (
      <ArrowDownLeft className="h-4 w-4 text-green-600" />
    ) : (
      <ArrowUpRight className="h-4 w-4 text-red-600" />
    );
  };

  const getTransactionColor = (type: string) => {
    return type === "credit" || type === "add" || type === "refund"
      ? "text-green-600"
      : "text-red-600";
  };

  const formatAmount = (amount: number, type: string) => {
    const prefix = type === "credit" || type === "add" || type === "refund" ? "+" : "-";
    return `${prefix}${formatCurrency(Math.abs(amount))}`;
  };

  const getStatusBadge = (status?: string) => {
    const statusConfig = {
      completed: {
        label: "Completed",
        className: "bg-green-500/20 text-green-600",
      },
      pending: {
        label: "Pending",
        className: "bg-yellow-500/20 text-yellow-600",
      },
      failed: { label: "Failed", className: "bg-red-500/20 text-red-600" },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge className={`text-xs ${config.className}`}>{config.label}</Badge>
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-96 p-0"
        align="end"
        side="bottom"
        sideOffset={8}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold">Wallet Overview</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadWalletData(true)}
              disabled={refreshing}
              className="h-7 w-7 p-0 ml-auto"
            >
              <RefreshCw
                className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              />
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <div className="text-red-500 mb-3">
                <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
                <p className="text-sm">{error}</p>
              </div>
              <Button
                onClick={() => loadWalletData()}
                variant="outline"
                size="sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Balance Section */}
              {walletBalance && (
                <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 rounded-lg border">
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-blue-600">
                        ₹{walletBalance.balance.toFixed(2)}
                      </span>
                      <span className="text-muted-foreground text-sm">
                        Available
                      </span>
                    </div>

                    {walletBalance.pendingBalance > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Pending:</span>
                        <span className="font-medium text-orange-600">
                          +₹{walletBalance.pendingBalance.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  size="sm"
                  onClick={handleAddMoney}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Money
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleViewFullWallet}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Wallet
                </Button>
              </div>

              <Separator />

              {/* Recent Transactions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Recent Transactions
                  </h4>
                  {recentTransactions.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      Last {recentTransactions.length}
                    </span>
                  )}
                </div>

                {recentTransactions.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Wallet className="h-6 w-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {recentTransactions.map((transaction) => {
                      const createdAt = transaction.createdAt ?? transaction.created_at;
                      const createdDate = new Date(createdAt ?? "");
                      const createdLabel = createdAt && !Number.isNaN(createdDate.getTime())
                        ? formatDistanceToNow(createdDate, { addSuffix: true })
                        : "-";

                      return (
                        <div
                          key={transaction.id}
                          className="flex items-center justify-between p-2 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {getTransactionIcon(transaction.type)}
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">
                                {transaction.description}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span>{createdLabel}</span>
                                {getStatusBadge(transaction.status)}
                              </div>
                            </div>
                          </div>

                          <div className="text-right ml-2">
                            <p
                              className={`font-semibold text-sm ${getTransactionColor(transaction.type)}`}
                            >
                              {formatAmount(transaction.amount, transaction.type)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {recentTransactions.length > 0 && (
                  <div className="mt-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleViewFullWallet}
                      className="text-xs h-8"
                    >
                      View all transactions
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
